import { resolveTime } from '../../../lib/formatDate'
import { useTimeFormat } from '../../../hooks/useTimeFormat'
import { cn } from '../../../lib/utils'
import type { SetTrim, TrimEdge } from './useSetTrim'

function TrimHandle({ edge, top, trim, color }: { edge: TrimEdge; top: number; trim: SetTrim; color: string }) {
  return (
    <div
      data-testid={`trim-handle-${edge}`}
      aria-hidden
      className="absolute inset-x-0 z-40 h-7 -translate-y-1/2 flex items-center justify-center pointer-events-auto touch-none cursor-ns-resize"
      // The card's border is 2px and drawn inside its box, so its centerline sits
      // 1px in from the window edge — center the grip on that, not the edge.
      style={{ top: `calc(${top * 100}% ${edge === 'start' ? '+' : '-'} 1px)` }}
      {...trim.handleProps(edge)}
    >
      <div className={cn('h-1.5 w-8 max-w-[70%] rounded-full shadow-md shadow-black/60 ring-2 ring-background animate-in fade-in zoom-in-0 duration-200 ease-out', color)} />
    </div>
  )
}

/**
 * Grips on the window's top and bottom edges, plus the live time readout. The
 * grips take the card's own status color so they read as its border made
 * grabbable, not as a separate control laid over it. The window's border is
 * redrawn here too: at rest it sits under the card's content, but while
 * trimming it's the thing being dragged, so nothing may cover it.
 */
export function SetTrimHandles({
  trim,
  duration,
  setStartTime,
  status,
}: {
  trim: SetTrim
  duration: number
  setStartTime: string
  status: 'interested' | 'going'
}) {
  const { formatClockRange } = useTimeFormat()
  if (!trim.draft) return null
  const [start, end] = trim.draft
  const color = status === 'going' ? 'bg-going' : 'bg-interested'
  return (
    <div className="pointer-events-none absolute inset-0 z-40">
      <div
        data-testid="trim-window-border"
        className={cn('absolute inset-x-0 rounded border-2', status === 'going' ? 'border-going' : 'border-interested')}
        style={{ top: `${(start / duration) * 100}%`, height: `${((end - start) / duration) * 100}%` }}
      />
      <TrimHandle edge="start" top={start / duration} trim={trim} color={color} />
      <TrimHandle edge="end" top={end / duration} trim={trim} color={color} />
      <div
        data-testid="trim-time-label"
        // Above the top grip: the dragging finger covers what's below it, so
        // pinned under the bottom grip the readout was hidden mid-drag.
        className="pointer-events-none absolute left-1/2 z-40 -translate-x-1/2 -translate-y-full -mt-3 whitespace-nowrap rounded-full border border-border bg-popover px-2 py-1 text-[10px] leading-none font-semibold tabular-nums text-popover-foreground shadow-md shadow-black/60 animate-in fade-in slide-in-from-bottom-1 duration-200 ease-out"
        style={{ top: `${(start / duration) * 100}%` }}
      >
        {formatClockRange(resolveTime(setStartTime, start), resolveTime(setStartTime, end))}
      </div>
    </div>
  )
}
